import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { word } from "@/modules/core/utils/i18n"
import { useCountrySubdivisionsListArray } from "@/modules/language/utils/countries"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { type FieldValueChange } from "@/typings/formik"
import { getEmojiFlag, type TCountryCode } from "countries-list"
import { type FieldInputProps } from "formik"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

export type PhoneNumberInputProps<T, K> = {
  label?: string
  name: string
  countryCodeName: string
  countryCode?: TCountryCode
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<string | number | null>
  isRequiredInput?: boolean
  error?: React.JSX.Element
  placeholder?: string
  labelClassName?: string
} & React.ComponentProps<"div">

export function PhoneNumberInput<T, K>({
  name,
  countryCodeName,
  label,
  countryCode,
  setFieldValue,
  getFieldProps,
  isRequiredInput,
  error,
  placeholder = word("global.form.placeholder.phoneNumber"),
  ...props
}: PhoneNumberInputProps<T, K>) {
  const [open, setOpen] = useState(false)
  const [phoneCountry, setPhoneCountry] = useState<TCountryCode | undefined>(countryCode)

  useEffect(() => {
    setPhoneCountry(countryCode)
  }, [countryCode])

  const countriesOptions = useCountrySubdivisionsListArray()

  const items = useMemo(
    () =>
      countriesOptions.map((countryOptions) => {
        if (countryOptions.code === "XK") {
          return {
            label:
              getEmojiFlag(countryOptions.code.toUpperCase() as TCountryCode) + " " + countryOptions.label + " (+383)",
            shortLabel: getEmojiFlag(countryOptions.code.toUpperCase() as TCountryCode) + " (+383)",
            value: countryOptions.code,
          }
        }
        return {
          label:
            getEmojiFlag(countryOptions.code.toUpperCase() as TCountryCode) +
            " " +
            countryOptions.label +
            " (+" +
            String(countryOptions.phone) +
            ")",
          shortLabel:
            getEmojiFlag(countryOptions.code.toUpperCase() as TCountryCode) +
            " (+" +
            countryOptions.phone.toString() +
            ")",
          value: countryOptions.code,
        }
      }),
    [countriesOptions],
  )

  return (
    <InputContainer isRequiredInput={isRequiredInput} label={label} name={name} error={error} {...props}>
      <div className="flex grow flex-row gap-2.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="h-11 min-w-30 shrink justify-between bg-transparent hover:border-interactive-border-primary-default hover:bg-transparent"
              >
                {phoneCountry
                  ? items.find((countryOptions) => countryOptions.value === phoneCountry)?.shortLabel
                  : word("box.form.address.country.label")}

                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-65 border-0 p-0" align="start">
            <Command
              className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
              filter={(value, search) => {
                if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase()))) return 1
                return 0
              }}
            >
              <CommandInput placeholder={word("global.searchCountry")} className="" />
              <CommandList>
                <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {items.map((countryOption) => (
                    <CommandItem
                      key={countryOption.value}
                      value={countryOption.label}
                      onSelect={(currentValue) => {
                        const foundCountry = items.find((item) => item.label === currentValue)
                        setPhoneCountry(foundCountry?.value as TCountryCode)
                        void setFieldValue(countryCodeName, foundCountry?.value as TCountryCode)
                        setOpen(false)
                      }}
                      className="cursor-pointer hover:bg-neutral-200"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          phoneCountry === countryOption.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {countryOption.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <StandardInput
          name={name}
          isRequiredInput
          getFieldProps={getFieldProps}
          setFieldValue={setFieldValue}
          containerClassName="flex flex-row items-center flex-grow-1"
          inputProps={{ placeholder }}
        />
      </div>
    </InputContainer>
  )
}
